import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Title, 
  Text, 
  Button, 
  Card, 
  Group, 
  Stack, 
  Badge,
  Grid,
  ThemeIcon,
  Alert,
  Loader,
  Box,
  useMantineColorScheme
} from '@mantine/core';
import { 
  IconUsers, 
  IconPlus, 
  IconLogin, 
  IconTrophy, 
  IconClock, 
  IconTarget,
  IconInfoCircle 
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { MultijugadorService } from '../services/multijugadorService';
import { WebSocketService } from '../services/webSocketService';
import CrearSalaModal from '../components/multijugador/CrearSalaModal';
import UnirseSalaModal from '../components/multijugador/UnirseSalaModal';

const MultijugadorPage: React.FC = () => {
  console.log('MULTIJUGADOR PAGE LOADED - IF YOU SEE THIS, LOGS ARE WORKING');
  console.log('🎮 [MULTIJUGADOR] ====== COMPONENTE MONTADO ======');

  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const [crearSalaModalOpen, setCrearSalaModalOpen] = useState(false);
  const [unirseSalaModalOpen, setUnirseSalaModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [salaActivaEncontrada, setSalaActivaEncontrada] = useState<any>(null);
  const [mostrandoAlertaSalaActiva, setMostrandoAlertaSalaActiva] = useState(false);
  const [serviceStatus, setServiceStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');

  useEffect(() => {
    console.log('🎮 [MULTIJUGADOR] useEffect: Iniciando verificación de servicio y sala activa');
    inicializarPagina();
  }, []);

  const inicializarPagina = async () => {
    // Verificar salud del servicio
    await checkServiceHealth();
    
    // Verificar sala activa solo si el servicio está disponible
    try {
      await verificarSalaActiva();
    } catch (error) {
      console.error('Error verificando sala activa:', error);
      // Continuamos normalmente aunque falle
    }
  };

  const checkServiceHealth = async () => {
    console.log('🔍 [MULTIJUGADOR] Verificando salud del servicio...');
    try {
      await MultijugadorService.healthCheck();
      console.log('✅ [MULTIJUGADOR] Servicio disponible');
      setServiceStatus('available');
    } catch (error) {
      console.error('❌ [MULTIJUGADOR] Servicio no disponible:', error);
      setServiceStatus('unavailable');
    }
  };

  const verificarSalaActiva = async () => {
    try {
      const salaActiva = await MultijugadorService.obtenerMiSalaActiva();
      if (salaActiva) {
        console.log('⚠️ Sala activa encontrada:', salaActiva);
        setSalaActivaEncontrada(salaActiva);
        setMostrandoAlertaSalaActiva(true);
      } else {
        // Limpiar estado si no hay sala activa
        console.log('✅ No hay sala activa (puede estar finalizada)');
        setSalaActivaEncontrada(null);
        setMostrandoAlertaSalaActiva(false);
      }
    } catch (error) {
      console.error('Error verificando sala activa:', error);
      // Si hay error, continuamos normalmente
    }
  };

  const handleCerrarSalaActiva = async () => {
    try {
      await MultijugadorService.cerrarMiSalaActiva();
      console.log('✅ Sala activa cerrada exitosamente');
      notifications.show({
        title: 'Sala cerrada',
        message: 'Tu sala anterior ha sido cerrada. Ahora puedes crear una nueva.',
        color: 'green',
      });
      setSalaActivaEncontrada(null);
      setMostrandoAlertaSalaActiva(false);
    } catch (error: any) {
      console.error('❌ Error cerrando sala activa:', error);
      notifications.show({
        title: 'Error',
        message: 'No se pudo cerrar la sala activa. Inténtalo de nuevo.',
        color: 'red',
      });
    }
  };

  const handleIrASalaActiva = () => {
    if (salaActivaEncontrada) {
      navigate(`/multijugador/sala/${salaActivaEncontrada.codigoSala}`, {
        state: { sala: salaActivaEncontrada, fromActive: true }
      });
    }
  };

  const handleCrearSala = async (datos: any) => {
    console.log('🎮 [MULTIJUGADOR] ====== CREAR SALA ======');
    console.log('📊 [MULTIJUGADOR] Datos de la sala:', datos);

    // Re-verificar sala activa antes de crear una nueva
    // (por si la sala anterior se finalizó mientras estábamos en esta página)
    await verificarSalaActiva();

    // Verificar si ya tiene una sala activa
    if (salaActivaEncontrada) {
      notifications.show({
        title: 'Ya tienes una sala activa',
        message: 'Debes cerrar tu sala actual antes de crear una nueva.',
        color: 'orange',
      });
      return;
    }
    setLoading(true);
    try {
      console.log('🌐 [MULTIJUGADOR] Llamando al servicio crearSala...');
      const sala = await MultijugadorService.crearSala(datos);
      console.log('✅ [MULTIJUGADOR] Sala creada exitosamente:', sala);
      console.log('   - Código de sala:', sala.codigoSala);
      console.log('   - Nombre:', sala.nombreSala);
      console.log('   - Estado:', sala.estado);

      notifications.show({
        title: '¡Sala creada!',
        message: `Tu sala "${sala.nombreSala}" está lista. Código: ${sala.codigoSala}`,
        color: 'green',
      });

      console.log('🔄 [MULTIJUGADOR] Navegando a la sala de espera...');
      console.log('   - URL: /multijugador/sala/' + sala.codigoSala);
      console.log('   - State:', { sala, fromCreation: true });

      // Navegar a la sala de espera
      navigate(`/multijugador/sala/${sala.codigoSala}`, {
        state: { sala, fromCreation: true }
      });
      console.log('✅ [MULTIJUGADOR] Navegación completada');
    } catch (error: any) {
      console.error('❌ [MULTIJUGADOR] Error al crear sala:', error);
      console.error('   - Mensaje:', error.message);
      console.error('   - Response:', error.response?.data);
      notifications.show({
        title: 'Error al crear sala',
        message: error.message,
        color: 'red',
      });
    } finally {
      setLoading(false);
      setCrearSalaModalOpen(false);
    }
  };

  const handleUnirseASala = async (codigoSala: string) => {
    console.log('🎮 [MULTIJUGADOR] ====== UNIRSE A SALA ======');
    console.log('📊 [MULTIJUGADOR] Código de sala:', codigoSala);
    setLoading(true);
    try {
      console.log('🌐 [MULTIJUGADOR] Llamando al servicio unirseASala...');
      const sala = await MultijugadorService.unirseASala({ codigoSala });
      console.log('✅ [MULTIJUGADOR] Unido a sala exitosamente:', sala);
      console.log('   - Código de sala:', sala.codigoSala);
      console.log('   - Nombre:', sala.nombreSala);
      console.log('   - Participantes:', sala.participantes.length);

      notifications.show({
        title: '¡Te uniste a la sala!',
        message: `Bienvenido a "${sala.nombreSala}"`,
        color: 'green',
      });

      console.log('🔄 [MULTIJUGADOR] Navegando a la sala de espera...');
      console.log('   - URL: /multijugador/sala/' + sala.codigoSala);
      console.log('   - State:', { sala, fromJoin: true });

      // Navegar a la sala de espera
      navigate(`/multijugador/sala/${sala.codigoSala}`, {
        state: { sala, fromJoin: true }
      });
      console.log('✅ [MULTIJUGADOR] Navegación completada');
    } catch (error: any) {
      console.error('❌ [MULTIJUGADOR] Error al unirse a sala:', error);
      console.error('   - Mensaje:', error.message);
      console.error('   - Response:', error.response?.data);
      notifications.show({
        title: 'Error al unirse a la sala',
        message: error.message,
        color: 'red',
      });
    } finally {
      setLoading(false);
      setUnirseSalaModalOpen(false);
    }
  };

if (serviceStatus === 'checking') {
    return (
      <Box style={{
        padding: '2rem',
        minHeight: '100vh',
        backgroundColor: colorScheme === 'dark'
          ? 'rgba(15, 23, 42, 0.5)'
          : 'rgba(247, 243, 238, 0.3)'
      }}>
        <Container size="lg" py="xl">
          <div className="flex justify-center items-center h-64">
            <Stack align="center" gap="md">
              <Loader size="lg" />
              <Text>Verificando disponibilidad del servicio...</Text>
            </Stack>
          </div>
        </Container>
      </Box>
    );
  }

if (serviceStatus === 'unavailable') {
    return (
      <Box style={{
        padding: '2rem',
        minHeight: '100vh',
        backgroundColor: colorScheme === 'dark'
          ? 'rgba(15, 23, 42, 0.5)'
          : 'rgba(247, 243, 238, 0.3)'
      }}>
        <Container size="lg" py="xl">
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="Servicio no disponible"
            color="orange"
          >
            El sistema multijugador no está disponible en este momento.
            Por favor, inténtalo más tarde.
          </Alert>
        </Container>
      </Box>
    );
  }

return (
    <Box style={{
      padding: '16px 24px',
      minHeight: '100vh',
      overflow: 'hidden',
      overflowY: 'auto',
      backgroundColor: colorScheme === 'dark'
        ? 'rgba(15, 23, 42, 0.5)'
        : 'rgba(247, 243, 238, 0.3)'
    }}>
        {/* Alerta de sala activa */}
        {mostrandoAlertaSalaActiva && salaActivaEncontrada && (
          <Alert 
            color="orange" 
            title="¡Ya tienes una sala activa!"
            icon={<IconInfoCircle size={16} />}
            withCloseButton={false}
            mb="md"
          >
            <Text size="sm" mb="md">
              Tienes la sala <strong>"{salaActivaEncontrada.nombreSala}"</strong> 
              (código: <strong>{salaActivaEncontrada.codigoSala}</strong>) activa.
            </Text>
            <Group gap="sm">
              <Button 
                size="xs" 
                variant="outline" 
                color="orange"
                onClick={handleIrASalaActiva}
              >
                Ir a mi sala
              </Button>
              <Button 
                size="xs" 
                variant="filled" 
                color="red"
                onClick={handleCerrarSalaActiva}
              >
                Cerrar sala y crear nueva
              </Button>
            </Group>
          </Alert>
        )}
        
        <Stack gap="md">
          {/* Header */}
          <Box mb="sm" ta="left">
            <Title
              order={2}
              size="1.3rem"
              fw={600}
              ta="left"
              style={{
                color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                fontFamily: 'Space Grotesk, Inter, sans-serif',
                marginBottom: '4px',
              }}
            >
              Modo Multijugador
            </Title>
            <Text
              size="xs"
              ta="left"
              style={{
                color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Compite en tiempo real con otros estudiantes y demuestra tus conocimientos
            </Text>
          </Box>

{/* Acciones principales */}
        <Grid gutter="md">
          {/* Crear sala */}
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Card shadow="sm" padding="md" radius="md" withBorder>
              <Group gap="sm" mb="xs">
                <ThemeIcon size={48} radius="md" variant="light" color="blue">
                  <IconPlus size={24} />
                </ThemeIcon>
                <div style={{ flex: 1 }}>
                  <Text fw={600} size="md">Crear Sala</Text>
                  <Text size="xs" c="dimmed">Configura y comparte</Text>
                </div>
              </Group>
              <Button
                fullWidth
                size="sm"
                variant="filled"
                color="blue"
                onClick={() => setCrearSalaModalOpen(true)}
                loading={loading}
                disabled={loading || salaActivaEncontrada !== null}
              >
                {salaActivaEncontrada ? 'Ya tienes sala activa' : 'Crear Nueva Sala'}
              </Button>
            </Card>
          </Grid.Col>

          {/* Unirse a sala */}
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Card shadow="sm" padding="md" radius="md" withBorder>
              <Group gap="sm" mb="xs">
                <ThemeIcon size={48} radius="md" variant="light" color="green">
                  <IconLogin size={24} />
                </ThemeIcon>
                <div style={{ flex: 1 }}>
                  <Text fw={600} size="md">Unirse a Sala</Text>
                  <Text size="xs" c="dimmed">Usa un código</Text>
                </div>
              </Group>
              <Button
                fullWidth
                size="sm"
                variant="filled"
                color="green"
                onClick={() => setUnirseSalaModalOpen(true)}
                loading={loading}
                disabled={loading}
              >
                Unirse con Código
              </Button>
            </Card>
          </Grid.Col>
        </Grid>

{/* Información adicional */}
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group gap="sm" mb="sm">
            <ThemeIcon size={32} radius="md" variant="light" color="blue">
              <IconTarget size={18} />
            </ThemeIcon>
            <Text fw={600} size="sm">¿Cómo funciona?</Text>
          </Group>
          <Stack gap="xs">
            <Text size="xs">
              <strong>1. Crear o unirse:</strong> Crea una sala o usa un código de 6 caracteres.
            </Text>
            <Text size="xs">
              <strong>2. Configurar:</strong> El creador elige preguntas, especialidades y dificultad.
            </Text>
            <Text size="xs">
              <strong>3. Competir:</strong> Todos responden las mismas preguntas en tiempo real.
            </Text>
            <Text size="xs">
              <strong>4. Ganar:</strong> Puntuación basada en aciertos y velocidad de respuesta.
            </Text>
          </Stack>
        </Card>

        {/* Características */}
        <Grid gutter="xs">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Card shadow="xs" padding="sm" radius="md" withBorder>
              <Group gap="xs">
                <ThemeIcon size={32} radius="md" variant="light" color="blue">
                  <IconUsers size={16} />
                </ThemeIcon>
                <div>
                  <Text fw={500} size="xs">Hasta 10 jugadores</Text>
                  <Text size="xs" c="dimmed">Compite con amigos</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Card shadow="xs" padding="sm" radius="md" withBorder>
              <Group gap="xs">
                <ThemeIcon size={32} radius="md" variant="light" color="green">
                  <IconClock size={16} />
                </ThemeIcon>
                <div>
                  <Text fw={500} size="xs">Tiempo real</Text>
                  <Text size="xs" c="dimmed">Resultados instantáneos</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Card shadow="xs" padding="sm" radius="md" withBorder>
              <Group gap="xs">
                <ThemeIcon size={32} radius="md" variant="light" color="orange">
                  <IconTrophy size={16} />
                </ThemeIcon>
                <div>
                  <Text fw={500} size="xs">Sistema de puntos</Text>
                  <Text size="xs" c="dimmed">Bonus por velocidad</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>

      {/* Modales */}
      <CrearSalaModal
        opened={crearSalaModalOpen}
        onClose={() => setCrearSalaModalOpen(false)}
        onSubmit={handleCrearSala}
        loading={loading}
      />

      <UnirseSalaModal
        opened={unirseSalaModalOpen}
        onClose={() => setUnirseSalaModalOpen(false)}
        onSubmit={handleUnirseASala}
        loading={loading}
      />
    </Box>
  );
};

export default MultijugadorPage;